import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { CqmEvidenceBundleClient, CqmEvidenceBundleRunDto, CqmEvidenceBundleRunsSearchDto, CreateCqmEvidenceBundleRequest } from '@medone/medonehp-api-client';

import { Axios } from '../../../shared/common/http';
import { RootState } from '../../../shared/store';
import { initialEvidenceBundleState } from './evidence-models';

const buildClient = () => new CqmEvidenceBundleClient(null as any, Axios);

// The generated client throws ApiException on any non-2xx (status + parsed ProblemDetails on
// `.result`) — surface that backend message rather than Axios's generic "Request failed…".
const errorFrom = (e: any, fallback: string): string => e?.result?.title ?? e?.result?.detail ?? e?.response?.data?.title ?? e?.message ?? fallback;

export const generateEvidenceBundle = createAsyncThunk('cqmEvidenceBundle/generate', async (request: CreateCqmEvidenceBundleRequest) => {
    try {
        const response = await buildClient().generate(request);
        if (response.result.succeeded) {
            return response.result.entity!;
        }
        throw new Error(response.result.message || 'Failed to generate the evidence bundle.');
    } catch (e: any) {
        throw new Error(errorFrom(e, 'Failed to generate the evidence bundle.'));
    }
});

export const fetchEvidenceReports = createAsyncThunk(
    'cqmEvidenceBundle/fetchReports',
    async (search: Partial<CqmEvidenceBundleRunsSearchDto> & { current?: number; pageSize?: number }) => {
        const dto = CqmEvidenceBundleRunsSearchDto.fromJS({
            current: search.current ?? 1,
            pageSize: search.pageSize ?? 50,
            status: search.status,
            createdByMe: search.createdByMe,
        });
        const response = await buildClient().getReports(dto);
        if (response.result.succeeded) {
            return {
                items: response.result.entity ?? [],
                total: response.result.pagination?.total ?? response.result.entity?.length ?? 0,
            };
        }
        return { items: [] as CqmEvidenceBundleRunDto[], total: 0 };
    }
);

// Background/View fetch — a thrown 404/ApiException must not surface an error, just clear the run.
export const fetchEvidenceReport = createAsyncThunk('cqmEvidenceBundle/fetchReport', async (id: number) => {
    try {
        const response = await buildClient().getReport(id);
        return response.result.succeeded ? (response.result.entity ?? null) : null;
    } catch {
        return null;
    }
});

/**
 * Streams the evidence-bundle ZIP for a completed run and forces a browser download. Clones the
 * report-slice download blob + anchor-click pattern. The bytes are PHI — hold them in memory only,
 * never log them.
 */
export const downloadEvidenceBundle = createAsyncThunk('cqmEvidenceBundle/download', async (id: number) => {
    const response = await buildClient().downloadBundle(id);
    const file = response.result;
    if (!file?.data) return;

    const contentType = (file.headers && (file.headers['content-type'] || file.headers['Content-Type'])) || 'application/zip';
    const blob = new Blob([file.data], { type: contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', file.fileName || `evidence-bundle-${id}.zip`);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
});

export const deleteEvidenceReport = createAsyncThunk('cqmEvidenceBundle/delete', async (id: number) => {
    try {
        const response = await buildClient().delete(id);
        if (response.result.succeeded) {
            return id;
        }
        throw new Error(response.result.message || 'Delete failed.');
    } catch (e: any) {
        throw new Error(errorFrom(e, 'Delete failed.'));
    }
});

const slice = createSlice({
    name: 'cqmEvidenceBundle',
    initialState: initialEvidenceBundleState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchEvidenceReports.pending, (state) => {
                state.loading = true;
                state.errorMessage = null;
            })
            .addCase(fetchEvidenceReports.fulfilled, (state, action) => {
                state.loading = false;
                state.runs = action.payload.items;
                state.runsTotal = action.payload.total;
            })
            .addCase(fetchEvidenceReports.rejected, (state, action) => {
                state.loading = false;
                state.errorMessage = action.error.message ?? 'Failed to load evidence bundles.';
            })
            .addCase(fetchEvidenceReport.fulfilled, (state, action) => {
                state.currentRun = action.payload;
            })
            .addCase(generateEvidenceBundle.pending, (state) => {
                state.processing = true;
                state.errorMessage = null;
            })
            .addCase(generateEvidenceBundle.fulfilled, (state, action) => {
                state.processing = false;
                state.currentRun = action.payload;
            })
            .addCase(generateEvidenceBundle.rejected, (state, action) => {
                state.processing = false;
                state.errorMessage = action.error.message ?? 'Failed to generate the evidence bundle.';
            })
            .addCase(deleteEvidenceReport.fulfilled, (state, action) => {
                state.runs = state.runs.filter((r) => r.id !== action.payload);
                state.runsTotal = Math.max(0, state.runsTotal - 1);
                if (state.currentRun?.id === action.payload) {
                    state.currentRun = null;
                }
            });
    },
});

export const selectEvidenceRuns = (state: RootState) => (state as any).cqmEvidenceBundle?.runs ?? [];
export const selectEvidenceRunsTotal = (state: RootState) => (state as any).cqmEvidenceBundle?.runsTotal ?? 0;
export const selectCurrentEvidenceReport = (state: RootState) => (state as any).cqmEvidenceBundle?.currentRun ?? null;
export const selectEvidenceLoading = (state: RootState) => (state as any).cqmEvidenceBundle?.loading ?? false;
export const selectEvidenceProcessing = (state: RootState) => (state as any).cqmEvidenceBundle?.processing ?? false;
export const selectEvidenceError = (state: RootState) => (state as any).cqmEvidenceBundle?.errorMessage ?? null;

export default slice.reducer;
